import { APPOINTMENT_STATUS_LABELS, formatDuration, formatTime } from '@crm/shared';
import { Button, cn, Input, Label, Modal, Textarea } from '@crm/ui';
import {
  Building2,
  CalendarDays,
  Clock,
  IdCard,
  Pencil,
  Plus,
  Scissors,
  Ticket,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Avatar } from '@/components';
import { formatCurrency, formatDate } from '@/lib/utils';
import { toErrorMessage } from '@/modules/auth/utils';
import { useApplyCoupon } from '@/modules/coupons/api';
import { CustomerForm } from '@/modules/customers/components/customer-form';
import { AppointmentStatusBadge } from '../status-badge';
import { SearchList } from './search-list';
import type { AppointmentWizard, WizardRow } from './use-appointment-wizard';

const hour = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });

/**
 * Cartao do resumo: pouco arredondado e levemente elevado, como os widgets
 * do painel. Sem a sombra, os blocos sumiam no fundo da modal.
 */
const CARTAO =
  'rounded-card border border-border-strong bg-card shadow-[0_1px_2px_0_rgba(15,23,42,0.05),0_2px_6px_-2px_rgba(15,23,42,0.10)] dark:bg-white/[0.06] dark:shadow-[0_1px_2px_0_rgba(0,0,0,0.5)]';

/**
 * Linha do cupom no resumo. O codigo vai para a API, que responde se vale e
 * quanto abate - a tela nunca inventa desconto.
 */
function CouponRow({ wizard, editable }: { wizard: AppointmentWizard; editable: boolean }) {
  const [code, setCode] = useState('');
  const applyCoupon = useApplyCoupon();

  if (wizard.coupon) {
    return (
      <div className="flex items-center justify-between gap-3 text-base text-foreground">
        <span className="flex min-w-0 items-center gap-2">
          <Ticket className="h-4 w-4 shrink-0" />
          <span className="truncate font-mono">{wizard.coupon.code}</span>
        </span>

        <span className="flex shrink-0 items-center gap-2">
          <span className="font-medium text-emerald-700 dark:text-emerald-400">
            -{formatCurrency(wizard.couponDiscount)}
          </span>
          {editable ? (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Tirar cupom"
              onClick={() => wizard.setCoupon(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </span>
      </div>
    );
  }

  if (!editable) return null;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-3 text-base text-foreground">
        <span>Cupom</span>

        <span className="flex shrink-0 items-center gap-2">
          <Input
            aria-label="Codigo do cupom"
            placeholder="CODIGO"
            className="w-32 font-mono uppercase sm:h-10"
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
          />
          <Button
            variant="outline"
            className="sm:h-10 sm:px-3 sm:text-xs"
            disabled={!code || applyCoupon.isPending}
            onClick={() =>
              applyCoupon.mutate(
                {
                  code,
                  amount: wizard.subtotal - wizard.discountTotal,
                  customerId: wizard.customer?.id,
                  serviceIds: wizard.rows.map((row) => row.serviceId),
                },
                {
                  onSuccess: (applied) => {
                    wizard.setCoupon({
                      id: applied.couponId,
                      code: applied.code,
                      discount: applied.discount,
                    });
                    setCode('');
                  },
                },
              )
            }
          >
            Aplicar
          </Button>
        </span>
      </div>

      {applyCoupon.error ? (
        <p className="text-right text-xs text-destructive">{toErrorMessage(applyCoupon.error)}</p>
      ) : null}
    </div>
  );
}

/**
 * Preco de um servico da comanda. Preco e desconto sao o mesmo numero visto
 * de dois angulos: baixar o preco aqui vira desconto na linha, e mexer no
 * percentual devolve o preco novo. O desconto e inteiro no banco, entao o
 * valor digitado assenta no centavo mais proximo do percentual possivel.
 */
function RowPrice({ wizard, row }: { wizard: AppointmentWizard; row: WizardRow }) {
  const efetivo = Math.round(row.price * (1 - row.discountPercent / 100) * 100) / 100;
  const [texto, setTexto] = useState(String(efetivo));

  // Mexeram no percentual (ou trocaram o servico): o preco acompanha.
  useEffect(() => {
    setTexto(String(efetivo));
  }, [efetivo]);

  const aplicar = () => {
    const valor = Number(texto.replace(',', '.'));
    if (!Number.isFinite(valor) || row.price <= 0) {
      setTexto(String(efetivo));
      return;
    }

    wizard.setDiscount(row.key, (1 - valor / row.price) * 100);
  };

  return (
    <Input
      type="number"
      min="0"
      step="0.01"
      prefix="R$"
      aria-label={`Preco de ${row.name}`}
      className="w-32 text-right text-base font-semibold"
      value={texto}
      onChange={(event) => setTexto(event.target.value)}
      onBlur={aplicar}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
      }}
    />
  );
}

/**
 * Passo 4: o agendamento inteiro em uma tela. E tambem a unica tela de um
 * agendamento ja concluido ou cancelado, ai sem nenhum botao de edicao.
 */
export function StepSummary({ wizard }: { wizard: AppointmentWizard }) {
  const { editable, rows } = wizard;
  const [isEditingCustomer, setEditingCustomer] = useState(false);
  const [isChoosingUnit, setChoosingUnit] = useState(false);
  const [unitSearch, setUnitSearch] = useState('');
  // Desconto e decisao do salao: pela pagina publica o cliente ve o preco,
  // troca servico e horario, mas nao mexe em quanto vai pagar.
  const canDiscount = editable && !wizard.isPublic;

  return (
    <div className="space-y-5">
      {wizard.appointment ? (
        <div className="flex flex-wrap items-center gap-2">
          <AppointmentStatusBadge status={wizard.appointment.status} />
          {wizard.appointment.cancelReason ? (
            <span className="text-sm text-destructive">
              Motivo: {wizard.appointment.cancelReason}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Quem, onde e quando - o cabecalho do agendamento. A elevacao e a
          mesma dos widgets do painel: o cartao existe sobre o fundo. */}
      <div className={cn(CARTAO, 'space-y-3 p-4')}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            {/* A foto do cadastro no lugar do icone generico: quem esta no
                balcao reconhece o cliente antes de ler o nome. */}
            <Avatar
              name={wizard.customer?.name ?? '?'}
              image={wizard.customer?.image}
              className="h-11 w-11 text-base"
            />
            <div className="min-w-0">
              <p className="truncate text-lg font-semibold">
                {wizard.customer?.name ?? 'Sem cliente'}
              </p>
              {/* Telefone nao quebra no meio: numero partido nao se le. */}
              {wizard.customer?.phone ? (
                <p className="truncate whitespace-nowrap text-sm text-muted-foreground">
                  {wizard.customer.phone}
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            {/* A ficha do cliente nao depende do agendamento: mesmo num
                atendimento ja concluido da para corrigir telefone ou nome.
                So na operacao - pela pagina publica o cliente nao edita. */}
            {!wizard.isPublic && wizard.customer ? (
              <Button
                variant="ghost"
                aria-label="Cadastro completo do cliente"
                className="sm:h-8 sm:px-3 sm:text-xs"
                onClick={() => setEditingCustomer(true)}
              >
                <IdCard className="h-4 w-4" />
                <span className="hidden sm:inline">Cadastro</span>
              </Button>
            ) : null}

            {editable ? (
              <Button
                variant="ghost"
                className="sm:h-8 sm:px-3 sm:text-xs"
                onClick={() => wizard.goTo('customer')}
              >
                <Pencil className="h-4 w-4" />
                Trocar
              </Button>
            ) : null}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
            <Building2 className="h-4 w-4 shrink-0" />
            <span className="truncate">{wizard.unitName}</span>
          </p>

          {/* So com mais de uma unidade: com uma so nao ha o que trocar. */}
          {wizard.canChangeUnit ? (
            <Button
              variant="ghost"
              className="shrink-0 sm:h-8 sm:px-3 sm:text-xs"
              onClick={() => setChoosingUnit(true)}
            >
              <Pencil className="h-4 w-4" />
              Trocar
            </Button>
          ) : null}
        </div>

        {wizard.startAt && wizard.endAt ? (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border pt-3">
            <span className="flex items-center gap-2 text-base font-medium">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
              {formatDate(wizard.startAt.toISOString())}
            </span>
            <span className="text-base font-medium">
              {hour.format(wizard.startAt)} - {formatTime(wizard.endAt.toISOString())}
            </span>
            <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              {formatDuration(wizard.totalMinutes)}
            </span>
          </div>
        ) : null}
      </div>

      {/* A comanda: um cartao por servico, com profissional e hora. */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold tracking-wide text-muted-foreground">Servicos</h3>

        {rows.length === 0 ? (
          <p className="rounded-sm border border-dashed border-border bg-muted/40 px-4 py-8 text-center text-base text-muted-foreground">
            Nenhum servico lancado.
          </p>
        ) : (
          <ul className="space-y-3">
            {rows.map((row) => (
              <li
                key={row.key}
                // No monitor a linha do servico e uma so: servico, preco,
                // desconto e os botoes lado a lado. Empilhado, cada servico
                // custava meia tela de rolagem.
                className={cn(CARTAO, 'p-4 lg:flex lg:items-center lg:gap-4')}
              >
                <div className="flex items-center justify-between gap-3 lg:min-w-0 lg:flex-1">
                  <div className="flex min-w-0 items-center gap-3">
                    {/* Foto do servico em quadrado e a do profissional
                        redonda: e a mesma distincao do resto do sistema. A
                        altura acompanha as duas linhas de texto ao lado. */}
                    {row.image ? (
                      <img
                        src={row.image}
                        alt=""
                        className="h-12 w-12 shrink-0 border border-border object-cover"
                      />
                    ) : (
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-border bg-muted text-muted-foreground">
                        <Scissors className="h-5 w-5" />
                      </span>
                    )}

                    <div className="min-w-0">
                      {/* Trocar cola no nome: e o servico daquela linha que
                          se troca, e o botao longe dele parecia geral. */}
                      <div className="flex min-w-0 items-center gap-1">
                        <p className="truncate text-base font-medium">{row.name}</p>
                        {editable ? (
                          <Button
                            variant="ghost"
                            className="shrink-0 sm:h-8 sm:px-2 sm:text-xs"
                            onClick={() => wizard.editRow(row)}
                          >
                            <Pencil className="h-4 w-4" />
                            Trocar
                          </Button>
                        ) : null}
                      </div>
                      <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                        {row.professionalName ? (
                          <Avatar
                            name={row.professionalName}
                            image={row.professionalImage}
                            className="h-6 w-6 text-[10px]"
                          />
                        ) : null}
                        <span className="min-w-0 truncate">
                          {row.professionalName ?? 'Sem profissional'}
                          {row.startAt ? ` - ${hour.format(row.startAt)}` : ''} -{' '}
                          {formatDuration(row.durationMinutes)}
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* O preco de tabela fica ao lado, riscado: embaixo do
                      campo ele desalinhava a linha inteira. */}
                  <div className="flex shrink-0 items-center gap-2">
                    {row.discountPercent > 0 ? (
                      <span className="text-xs text-muted-foreground line-through">
                        {formatCurrency(row.price)}
                      </span>
                    ) : null}

                    {canDiscount ? (
                      <RowPrice wizard={wizard} row={row} />
                    ) : (
                      <p className="text-base font-semibold">
                        {formatCurrency(row.price * (1 - row.discountPercent / 100))}
                      </p>
                    )}
                  </div>
                </div>

                {editable ? (
                  <div className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-3 lg:mt-0 lg:shrink-0 lg:justify-end lg:border-0 lg:pt-0">
                    <div
                      className={cn(
                        'flex items-center gap-2 text-sm text-muted-foreground',
                        !canDiscount && 'hidden',
                      )}
                    >
                      <Label htmlFor={`discount-${row.key}`}>Desconto</Label>
                      <Input
                        id={`discount-${row.key}`}
                        type="number"
                        min="0"
                        max="100"
                        aria-label={`Desconto de ${row.name}`}
                        className="w-20 text-center sm:h-10"
                        value={row.discountPercent}
                        onChange={(event) =>
                          wizard.setDiscount(row.key, Number(event.target.value || 0))
                        }
                      />
                      %
                    </div>

                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Remover ${row.name}`}
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => wizard.removeRow(row.key)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {editable ? (
          <Button
            variant="outline"
            // Na cor da acao principal: e o unico botao que acrescenta algo a
            // comanda, e em cinza ele passava por moldura vazia.
            className="h-12 w-full justify-center border-dashed border-primary/50 bg-card text-base text-primary hover:bg-primary/10"
            onClick={wizard.startAddService}
          >
            <Plus className="h-5 w-5" />
            Adicionar servico
          </Button>
        ) : null}
      </section>

      {/* O que se paga: sobre o proprio cartao, e em corpo maior - cinza
          sobre cinza, a conta era a parte menos legivel da tela. */}
      {rows.length > 0 ? (
        <section className={cn(CARTAO, 'space-y-2 p-4')}>
          <div className="flex items-center justify-between text-base text-foreground">
            <span>Subtotal</span>
            <span className="font-medium">{formatCurrency(wizard.subtotal)}</span>
          </div>

          <div className="flex items-center justify-between gap-3 text-base text-foreground">
            <span>Descontos</span>
            {canDiscount ? (
              <Input
                type="number"
                min="0"
                step="0.01"
                aria-label="Desconto total em reais"
                // O R$ fica no campo: sem ele o numero solto nao diz se e
                // percentual ou dinheiro, ao lado de um desconto em %.
                prefix="R$"
                className="w-36 text-right text-emerald-700 dark:text-emerald-400 sm:h-10"
                value={wizard.discountTotal}
                onChange={(event) =>
                  wizard.applyUniformDiscount(
                    wizard.subtotal > 0
                      ? (Number(event.target.value || 0) / wizard.subtotal) * 100
                      : 0,
                  )
                }
              />
            ) : (
              <span className="font-medium text-foreground">
                {formatCurrency(wizard.discountTotal)}
              </span>
            )}
          </div>

          {/* Cupom: o codigo e validado pela API, que devolve o desconto. */}
          {!wizard.isPublic ? <CouponRow wizard={wizard} editable={editable} /> : null}

          {/* O cashback abate sozinho: o balcao nao escolhe quanto usar, o
              saldo entra como desconto ate o limite da compra. */}
          {!wizard.isPublic && (wizard.cashbackBalance > 0 || wizard.cashbackUsed > 0) ? (
            <div className="flex items-center justify-between gap-3 text-base text-foreground">
              <span className="flex min-w-0 flex-col">
                <span>Cashback</span>
                <span className="text-xs text-muted-foreground">
                  saldo do cliente: {formatCurrency(wizard.cashbackBalance)}
                </span>
              </span>

              <span className="shrink-0 font-medium text-emerald-700 dark:text-emerald-400">
                -{formatCurrency(wizard.cashbackUsed)}
              </span>
            </div>
          ) : null}

          <div className="flex items-center justify-between gap-3 border-t border-border pt-2">
            <span className="text-lg font-semibold">Total</span>
            {canDiscount ? (
              <Input
                type="number"
                min="0"
                step="0.01"
                aria-label="Total em reais"
                prefix="R$"
                className="h-12 w-36 text-right text-xl font-semibold"
                value={wizard.finalTotal}
                onChange={(event) =>
                  wizard.applyUniformDiscount(
                    wizard.subtotal > 0
                      ? ((wizard.subtotal -
                          (Number(event.target.value || 0) + wizard.cashbackUsed)) /
                          wizard.subtotal) *
                          100
                      : 0,
                  )
                }
              />
            ) : (
              <span className="text-xl font-semibold">{formatCurrency(wizard.finalTotal)}</span>
            )}
          </div>
        </section>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="wizard-notes">Observacoes</Label>
        <Textarea
          id="wizard-notes"
          disabled={!editable}
          placeholder="Preferencias do cliente, alergias, combinados..."
          value={wizard.notes}
          onChange={(event) => wizard.setNotes(event.target.value)}
        />
      </div>

      {/* Busca de unidade: a mesma lista dos outros passos, com a seta
          indicando que a linha e selecionavel. */}
      {isChoosingUnit ? (
        <Modal
          open
          onClose={() => setChoosingUnit(false)}
          title="Unidade"
          actions={[{ label: 'Fechar', variant: 'outline', onClick: () => setChoosingUnit(false) }]}
        >
          <SearchList
            placeholder="Buscar unidade"
            emptyLabel="Nenhuma unidade com esse nome."
            onSearch={setUnitSearch}
            items={wizard.units
              .filter((unit) => unit.name.toLowerCase().includes(unitSearch.toLowerCase()))
              .map((unit) => ({
                id: unit.id,
                title: unit.name,
                subtitle: unit.type === 'HEAD_OFFICE' ? 'Matriz' : 'Filial',
                active: unit.id === wizard.unitId,
              }))}
            onSelect={(id) => {
              wizard.chooseUnit(id);
              setChoosingUnit(false);
            }}
          />
        </Modal>
      ) : null}

      {/* Modal por cima da modal: salvar (ou fechar) devolve ao resumo. */}
      {isEditingCustomer && wizard.customer ? (
        <Modal
          open
          onClose={() => setEditingCustomer(false)}
          title="Cadastro do cliente"
          subtitle={wizard.customer.name}
          actions={[
            { label: 'Fechar', variant: 'outline', onClick: () => setEditingCustomer(false) },
            { label: 'Salvar', type: 'submit', formId: 'wizard-customer-form' },
          ]}
        >
          <CustomerForm
            id={wizard.customer.id}
            formId="wizard-customer-form"
            hideSubmit
            onSaved={(saved) => {
              wizard.updateCustomer({ id: saved.id, name: saved.name, phone: saved.phone });
              setEditingCustomer(false);
            }}
          />
        </Modal>
      ) : null}

      {!editable && wizard.appointment ? (
        <p className="rounded-sm border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          {`Um agendamento "${APPOINTMENT_STATUS_LABELS[wizard.appointment.status]}" nao pode mais ser editado.`}
        </p>
      ) : null}
    </div>
  );
}
